import { Alert, Button, Form, FormInstance, Popconfirm, Spin } from 'antd';
import { useAppDispatch, useAppSelector } from 'app/store';
import { uiModel } from 'entities/ui';
import {
  useDeleteSubstitutionMutation,
  useLazyGetSingleSubstitutionQuery,
  useUpdateSubstitutionMutation,
} from 'app/services/edpApiV1/substitutions/substitution';
import { useNotifications } from 'shared/hooks/use-notifications';
import { useEffect } from 'react';
import { DatePeriodField } from 'shared/ui/form/DatePeriodField';
import { ComponentModes } from 'shared/enums';
import { SubstitutionsModel, SubstitutionsUi } from 'entities/substitutions';
import { EmployeesUi } from 'entities/employees';
import { getErrorData } from 'shared/helpers/utils';
import { convertDate } from 'shared/helpers/date';
import { SERVER_DATE_MASK } from 'shared/constants';
import dayjs from 'dayjs';
import { SearchEmployeesField } from '../search-employees-field';
import { SubstitutionFormData } from '../../types';
import style from '../index.module.scss';
import { EmployeeVocations } from '../employee-vocations';

interface IProps {
  form: FormInstance<SubstitutionFormData>;
}

export const EditSubctitution: React.FC<IProps> = ({ form }) => {
  const dispatch = useAppDispatch();

  const { controlDrawer } = useAppSelector(uiModel.Substitutions.selectors.selectSubstitutions);
  const isEdit = controlDrawer?.mode === ComponentModes.edit;
  const substitutionId = controlDrawer?.substitution.id;

  const [updateSybstitutionTrigger, updateResult] = useUpdateSubstitutionMutation({
    selectFromResult: result => ({ ...result, error: getErrorData(result.error) }),
  });

  const { isSuccess: updateIsSucces, isError: updateIsError, error: updateError, reset: updateReset } = updateResult;

  const [deleteSybstitutionTrigger, deleteResult] = useDeleteSubstitutionMutation({
    selectFromResult: result => ({ ...result, error: getErrorData(result.error) }),
  });
  const {
    isSuccess: deleteIsSucces,
    isError: deleteIsError,
    isLoading: deleteIsLoading,
    error: deleteError,
    reset: deleteReset,
  } = deleteResult;

  const [getSubstitutionTrigger, { data: getSubstitutionData, isLoading: getSubstitutionIsLoading }] =
    useLazyGetSingleSubstitutionQuery();

  const onDelete = () => {
    if (controlDrawer?.substitution.id) {
      deleteSybstitutionTrigger(controlDrawer?.substitution.id);
    }
  };

  const onFinish = (data: SubstitutionFormData) => {
    if (substitutionId) {
      updateSybstitutionTrigger({
        requestId: substitutionId,
        validTo: convertDate(data.period[1], { format: SERVER_DATE_MASK }),
      });
    }
  };

  const onValuesChange = (val: SubstitutionFormData, values: SubstitutionFormData) => {
    const { period } = values;
    if (period[1]) {
      dispatch(
        uiModel.Substitutions.actions.setButtonDisabled(
          getSubstitutionData?.validTo === convertDate(period[1], { format: SERVER_DATE_MASK }),
        ),
      );
    } else {
      dispatch(uiModel.Substitutions.actions.setButtonDisabled(true));
    }
  };

  useNotifications(updateIsSucces, updateIsError, 'Данные успешно обновлены', updateError?.message, updateReset);
  useNotifications(deleteIsSucces, deleteIsError, 'Замещение удалено', deleteError?.message, deleteReset);

  useEffect(() => {
    if (deleteIsSucces || updateIsSucces) {
      dispatch(uiModel.Substitutions.actions.closeSubstitutionControlDrawer());
    }
  }, [deleteIsSucces, updateIsSucces]);

  useEffect(() => {
    if (substitutionId) {
      getSubstitutionTrigger(substitutionId);
    }

    if (getSubstitutionData && substitutionId) {
      if (controlDrawer.orgId) {
        dispatch(SubstitutionsModel.divisions.actions.toShowChangedDate(controlDrawer.orgId));
      }
      form.setFieldsValue({
        employeeId: `${getSubstitutionData.employee.lastName || ''} ${getSubstitutionData.employee.name || ''} ${
          getSubstitutionData.employee.middleName || ''
        }`,
        period: [
          dayjs(getSubstitutionData.validFrom, SERVER_DATE_MASK),
          dayjs(getSubstitutionData.validTo, SERVER_DATE_MASK),
        ],
      });

      dispatch(uiModel.Substitutions.actions.setSelectedEymployerId(getSubstitutionData?.employee.id));
    }
  }, [substitutionId, getSubstitutionData]);

  return (
    <>
      <div className={style.author}>
        <h4>Автор замещения:</h4>
        {getSubstitutionIsLoading ? (
          <Spin size="small" />
        ) : (
          <>
            {getSubstitutionData && (
              <EmployeesUi.SummaryInfo
                user={getSubstitutionData?.creator}
                initials={true}
                showPersonnelNumber={false}
              />
            )}
            <div className={style.position}>{getSubstitutionData?.creator.position || ''}</div>
          </>
        )}
      </div>
      {isEdit && (
        <Alert
          showIcon
          message="Если вы хотите поменять замещающего, необходимо удалить данную заявку и создать новую"
        />
      )}
      <Form layout="vertical" form={form} onFinish={onFinish} onValuesChange={onValuesChange}>
        <SearchEmployeesField
          divisionId={controlDrawer?.substitution.id}
          disabled={true}
          loading={getSubstitutionIsLoading}
          label="Замещающий"
        />
        {isEdit && <EmployeeVocations />}
        <DatePeriodField disabled={[true, isEdit ? false : true]} loading={getSubstitutionIsLoading} />
      </Form>
      {isEdit && (
        <div className={style.btnWrap}>
          <Popconfirm
            title="Вы уверены, что хотите удалить замещение?"
            onConfirm={onDelete}
            okText="Удалить"
            cancelText="Назад"
          >
            <Button type="primary" loading={deleteIsLoading}>
              Удалить замещение
            </Button>
          </Popconfirm>
        </div>
      )}
      {substitutionId && <SubstitutionsUi.ChangesHistory substitutionId={substitutionId} />}
    </>
  );
};
